import { FormattedMessage, useIntl } from 'react-intl'
import BasePageLayout from '../../../common/components/BasePageLayout'
import PageHeader from '../../../../../components/InternalAppShell/PageHeader'
import { Cog6ToothIcon } from '@heroicons/react/24/outline'
import messages from './messages'
import SecretarySettings from '../../components/SecretarySettings/SecretarySettings'
import { Form } from 'react-final-form'
import InputField from '../../../../../components/common/form/InputField'
import {
  Button,
  Divider,
  Flex,
  Grid,
  useToast,
  Box,
  VStack,
  Heading,
  SimpleGrid,
  Avatar,
  Text,
  Stack,
  Icon
} from '@chakra-ui/react'
import RadioField from '../../../../../components/common/form/RadioField'
import { useAccount } from '../../../account/AccountProvider'
import { getDoctorsService } from '../../../../../services/Services'
import { useAuthState } from '../../../../../auth/AuthProvider'
import {
  FaUser,
  FaMapMarkerAlt,
  FaCreditCard,
  FaUserTie,
  FaCalendarAlt
} from 'react-icons/fa'
import AppointmentSettingsSection from '../../components/AppointmentSettings'
import { useSearchParams } from 'react-router-dom'

type SettingsSection = 'general' | 'secretary' | 'calendar'

const SETTINGS_SECTIONS: SettingsSection[] = [
  'general',
  'secretary',
  'calendar'
]

export default function Settings() {
  const toast = useToast()
  const { uid } = useAuthState()
  const { formatMessage } = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedSection = searchParams.get('section') as SettingsSection | null
  const activeSection =
    requestedSection && SETTINGS_SECTIONS.includes(requestedSection)
      ? requestedSection
      : 'general'

  const {
    firstName,
    lastName,
    phone,
    address,
    city,
    gender,
    visitPrice,
    followUpPrice
  } = useAccount()

  const initialValues = {
    firstName,
    lastName,
    phone,
    address,
    city,
    gender,
    visitPrice,
    followUpPrice
  }

  const handleUpdateProfileClick = async (values: any) => {
    try {
      await getDoctorsService().updateDoctorProfile({
        ownerId: uid,
        doctorData: values
      })
      toast({
        title: formatMessage(messages.saveSuccess),
        status: 'success',
        duration: 5000,
        isClosable: true
      })
    } catch (error) {
      toast({
        title: formatMessage(messages.saveError),
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  const navigationItems = [
    {
      id: 'general' as const,
      label: formatMessage({ id: 'settings.nav.general' }),
      icon: FaUser
    },
    {
      id: 'secretary' as const,
      label: formatMessage({ id: 'settings.nav.secretary' }),
      icon: FaUserTie
    },
    {
      id: 'calendar' as const,
      label: formatMessage({ id: 'settings.nav.calendar' }),
      icon: FaCalendarAlt
    }
  ]

  return (
    <BasePageLayout
      isCentered
      contentMode="wide"
      title={formatMessage(messages.pageTitle)}
    >
      <Box>
        <PageHeader
          title={formatMessage(messages.pageTitle)}
          description={formatMessage({ id: 'page.settingsDescription' })}
          icon={Cog6ToothIcon}
        />

        <Grid
          mt={6}
          templateColumns={{
            base: 'minmax(0, 1fr)',
            md: 'minmax(0, 1fr)'
          }}
          gap={{ base: 4, md: 6 }}
          alignItems="start"
        >
          <Flex
            as="nav"
            aria-label={formatMessage({ id: 'settings.sectionNavigation' })}
            role="tablist"
            direction="row"
            gap={1}
            pb={0}
            borderBottomWidth="1px"
            borderColor="gray.200"
            overflowX={{ base: 'auto', md: 'visible' }}
          >
            {navigationItems.map((item) => {
              const isActive = activeSection === item.id
              return (
                <Button
                  key={item.id}
                  id={`settings-tab-${item.id}`}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`settings-panel-${item.id}`}
                  size="sm"
                  flexShrink={0}
                  w="auto"
                  borderRadius={0}
                  borderBottomWidth="2px"
                  borderColor={isActive ? 'gray.900' : 'transparent'}
                  justifyContent="flex-start"
                  gap={2.5}
                  variant="ghost"
                  bg="transparent"
                  color={isActive ? 'gray.900' : 'gray.500'}
                  fontWeight={isActive ? '700' : '600'}
                  _hover={{
                    bg: 'gray.100',
                    color: 'gray.900'
                  }}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams)
                    next.set('section', item.id)
                    setSearchParams(next)
                  }}
                >
                  <Icon as={item.icon} boxSize="14px" />
                  <Text as="span" fontSize="sm">
                    {item.label}
                  </Text>
                </Button>
              )
            })}
          </Flex>

          <Box minW={0}>
            <Box
              maxW="900px"
              id="settings-panel-general"
              role="tabpanel"
              aria-labelledby="settings-tab-general"
              display={activeSection === 'general' ? 'block' : 'none'}
            >
              <Form
                initialValues={initialValues}
                onSubmit={(values) => {
                  handleUpdateProfileClick(values)
                }}
                render={({ handleSubmit, dirty, submitting }) => (
                  <form onSubmit={handleSubmit}>
                    <VStack spacing={6} align="stretch">
                      {/* Profile Header */}
                      <Flex align="center" gap={4} p={2}>
                        <Avatar
                          size="lg"
                          name={`${firstName} ${lastName}`}
                          bg="gray.900"
                          color="white"
                        />
                        <Box>
                          <Heading size="lg">
                            {firstName} {lastName}
                          </Heading>
                          <Text color="gray.500">
                            {formatMessage(messages.pageTitle)}
                          </Text>
                        </Box>
                      </Flex>

                      {/* Personal Information */}
                      <Box
                        p={6}
                        bg="white"
                        boxShadow="none"
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor="gray.200"
                      >
                        <Stack spacing={4}>
                          <Flex align="center" gap={2} mb={2}>
                            <Icon as={FaUser} color="gray.600" />
                            <Heading size="sm" fontWeight="semibold">
                              {formatMessage({
                                id: 'page.personalInformation'
                              })}
                            </Heading>
                          </Flex>
                          <Divider />
                          <SimpleGrid
                            columns={{ base: 1, md: 2 }}
                            spacing={4}
                            pt={2}
                          >
                            <InputField
                              name="firstName"
                              label={formatMessage(messages.firstName)}
                            />
                            <InputField
                              name="lastName"
                              label={formatMessage(messages.lastName)}
                            />
                          </SimpleGrid>
                          <InputField
                            name="phone"
                            label={formatMessage(messages.phone)}
                          />
                          <RadioField
                            name="gender"
                            label={formatMessage(messages.gender)}
                            options={[
                              {
                                title: formatMessage(messages.genderMale),
                                value: 'M'
                              },
                              {
                                title: formatMessage(messages.genderFemale),
                                value: 'F'
                              }
                            ]}
                          />
                        </Stack>
                      </Box>

                      {/* Contact & Location */}
                      <Box
                        p={6}
                        bg="white"
                        boxShadow="none"
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor="gray.200"
                      >
                        <Stack spacing={4}>
                          <Flex align="center" gap={2} mb={2}>
                            <Icon as={FaMapMarkerAlt} color="gray.600" />
                            <Heading size="sm" fontWeight="semibold">
                              {formatMessage({ id: 'page.contactLocation' })}
                            </Heading>
                          </Flex>
                          <Divider />
                          <SimpleGrid
                            columns={{ base: 1, md: 2 }}
                            spacing={4}
                            pt={2}
                          >
                            <InputField
                              name="address"
                              label={formatMessage(messages.address)}
                            />
                            <InputField
                              name="city"
                              label={formatMessage(messages.city)}
                            />
                          </SimpleGrid>
                        </Stack>
                      </Box>

                      {/* Pricing & Billing */}
                      <Box
                        p={6}
                        bg="white"
                        boxShadow="none"
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor="gray.200"
                      >
                        <Stack spacing={4}>
                          <Flex align="center" gap={2} mb={2}>
                            <Icon as={FaCreditCard} color="gray.600" />
                            <Heading size="sm" fontWeight="semibold">
                              {formatMessage({ id: 'page.pricing' })}
                            </Heading>
                          </Flex>
                          <Divider />
                          <SimpleGrid
                            columns={{ base: 1, md: 2 }}
                            spacing={4}
                            pt={2}
                          >
                            <InputField
                              type="number"
                              name="visitPrice"
                              label={formatMessage(messages.visitPrice)}
                              unit="EGP"
                            />
                            <InputField
                              type="number"
                              name="followUpPrice"
                              label={formatMessage(messages.followUpPrice)}
                              unit="EGP"
                            />
                          </SimpleGrid>
                        </Stack>
                      </Box>

                      <Flex justify="flex-start">
                        <Button
                          type="submit"
                          colorScheme="blue"
                          size="md"
                          isDisabled={!dirty}
                          isLoading={submitting}
                          loadingText="Saving..."
                        >
                          <FormattedMessage {...messages.saveProfile} />
                        </Button>
                      </Flex>
                    </VStack>
                  </form>
                )}
              />
            </Box>

            <Box
              id="settings-panel-secretary"
              role="tabpanel"
              aria-labelledby="settings-tab-secretary"
              display={activeSection === 'secretary' ? 'block' : 'none'}
            >
              <SecretarySettings />
            </Box>

            <Box
              id="settings-panel-calendar"
              role="tabpanel"
              aria-labelledby="settings-tab-calendar"
              display={activeSection === 'calendar' ? 'block' : 'none'}
            >
              <AppointmentSettingsSection />
            </Box>
          </Box>
        </Grid>
      </Box>
    </BasePageLayout>
  )
}
